/**
 * @override
 * Accordion component with lazy loading Rich Text links support
 */

import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Skeleton,
  SkeletonText,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';
import {PlusIcon, MinusIcon} from '@amblique/king-living/app/components/icons';
import {useAccordionLinks} from '@amblique/king-living/app/contentful/hooks/use-accordion-links';
import {hasEmbeddedContent} from '@amblique/king-living/app/contentful/utils/rich-text';

const buildClassName = (...parts) => parts.filter(Boolean).join(' ');

const Accordion = ({component, isComplete = true, isFetching = false, className, ...props}) => {
  const styles = useMultiStyleConfig('contentful/accordion', {component, isComplete, isFetching});

  const {title, body} = component;
  const hasContent = body?.json;

  // Check if we need to fetch links (only if body has embedded entries/assets and no links provided)
  const needsLinks = useMemo(() => {
    if (!hasContent || body?.links) return false;

    return hasEmbeddedContent(body.json);
  }, [hasContent, body]);

  // Lazy load links only when needed (with error handling)
  const {links, isLoading: isLoadingLinks, hasError: linksError} = useAccordionLinks(component.sys?.id, {
    enabled: needsLinks,
  });

  // Merge body with fetched links (skip if links failed - content will render without embedded entries)
  const bodyWithLinks = useMemo(() => {
    if (!hasContent) return body;
    if (body?.links) return body; // Already has links
    if (!links) return body; // No links or links failed - render without embedded entries

    return {
      ...body,
      links,
    };
  }, [body, links, hasContent]);

  // Don't render if no title or body
  if (!title && !hasContent && isComplete) {
    return null;
  }

  // Generate unique IDs for accessibility
  const accordionId = component.sys?.id || `accordion-${Math.random().toString(36).substr(2, 9)}`;
  const buttonId = `${accordionId}-button`;
  const panelId = `${accordionId}-panel`;

  // Show loading state only if fetching links AND not errored (if errored, show content without embedded)
  const isLoading = (!isComplete && isFetching) || (needsLinks && isLoadingLinks && !linksError);

  return (
    <Box
      {...styles.root}
      {...props}
      className={buildClassName('contentful-accordion', className)}
    >
      {isLoading ? (
        <>
          <Skeleton height="48px" mb={2} width="100%" />
          <SkeletonText noOfLines={3} spacing={2} />
        </>
      ) : (
        <AccordionItem className="contentful-accordion__item" {...styles.item}>
          {({isExpanded}) => (
            <>
              <h3 className="contentful-accordion__heading">
                <AccordionButton
                  className="contentful-accordion__button"
                  {...styles.button}
                  aria-controls={panelId}
                  aria-expanded={isExpanded}
                  id={buttonId}
                >
                  {title && (
                    <Box as="span" className="contentful-accordion__title" {...styles.title}>
                      {title}
                    </Box>
                  )}
                  <Box className="contentful-accordion__icon" {...styles.icon}>
                    {isExpanded ? (
                      <MinusIcon height="100%" width="100%" />
                    ) : (
                      <PlusIcon height="100%" width="100%" />
                    )}
                  </Box>
                </AccordionButton>
              </h3>
              {hasContent && (
                <AccordionPanel
                  className="contentful-accordion__panel"
                  {...styles.panel}
                  aria-labelledby={buttonId}
                  id={panelId}
                >
                  <RichTextContent className="contentful-accordion__body" content={bodyWithLinks} {...styles.content} />
                </AccordionPanel>
              )}
            </>
          )}
        </AccordionItem>
      )}
    </Box>
  );
};

Accordion.displayName = 'Accordion';
Accordion.propTypes = {
  className: PropTypes.string,
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};

export default Accordion;
export {Accordion};
